import React from 'react';
import ListItem from '@mui/material/ListItem';
import List from '@mui/material/List';
import ListItemText from '@mui/material/ListItemText';
import { Button, ListItemButton, ListItemIcon, Divider, Typography } from '@mui/material';
import { usePetOwnerPets } from '../../../hooks/patients/usePets';
import LoadingSpinner from '../../common/controls/LoadingSpinner';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import Box from '@mui/material/Box';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { useDeletePet } from '../../../hooks/patients/useDeletePet';
import PetView from '../modals/PetView';
import PetForm from '../forms/PetForm';
import PetsIcon from '@mui/icons-material/Pets';
import { getPetById } from '../../../api/petService';

export default function PetOwnerPetsList({ setPetOwners, successCallback, petOwner = null, trigger}) {

    const { t } = useTranslation('common');

    const [viewOpen, setViewOpen] = React.useState(false);

    const [selectedPet, setSelectedPet] = React.useState(null);

    const [petFormOpen, setPetFormOpen] = React.useState(false);
    const [petFormMode, setPetFormMode] = React.useState("create");

    const { pets, loading, searchFunction, resetParent } = usePetOwnerPets(petOwner.id, trigger);

    const { remove } = useDeletePet(successCallback);

    const { showSnackbar } = useSnackbar();

    const handleListItemClick = (pet) => {
        setSelectedPet(pet);
        setViewOpen(true);
    };

    const handleAddPet = () => {
        setSelectedPet(null);
        setPetFormMode("create");
        setPetFormOpen(true);
    };

    const handleEditPet = (pet) => {
        setSelectedPet(pet);
        setPetFormMode("edit");
        setPetFormOpen(true);
    };

    const onSavePetSuccess = async (savedPet) => {
        await resetParent();
        if (setPetOwners) setPetOwners()
        const updatedPet = await getPetById(savedPet.id);
        setSelectedPet(updatedPet);
        if (!viewOpen) setViewOpen(true);
    };

    const handleRemove = async (pet) => {
        try {
            await remove(pet.id);
            showSnackbar(t('pets.messages.delete_success', { name: pet.name }), "success");
        } catch (err) {
            showSnackbar(t('pets.messages.delete_fail', { name: pet.name }), "error");
        }
    };

    if (loading) {
        return (
            <LoadingSpinner />
        );
    }

    return(
        <>
        {pets.data.length === 0 ? 
          <Divider variant="fullWidth" component="li">
            <Typography variant="caption" color="text.secondary">
              no pets
            </Typography>
          </Divider>
        : null}
        <List>
            {pets.data.map((pet) => (
            <ListItem
                key={pet.id}
                secondaryAction={
                    <Box sx={{ ml: 2, display: 'flex', alignItems: 'right' }}>
                        <VerticalHamburgerMenu 
                            options={[
                            {
                                id: 1,
                                name: 'View',
                                callback: () => handleListItemClick(pet)
                            },
                            {
                                id: 2,
                                name: 'Edit',
                                callback: () => handleEditPet(pet)
                            },
                            {
                                id: 3,
                                name: 'Delete',
                                callback: () => handleRemove(pet),
                                confirmMessage: t('pets.messages.delete_confirm', { name: pet.name }),
                            },
                            ]}
                        />
                    </Box>
                }
            >
                <ListItemButton onClick={() => handleListItemClick(pet)}>
                    <ListItemIcon>
                        <PetsIcon fontSize='large' color='primary' />
                    </ListItemIcon>
                    <ListItemText 
                        primary={pet.name} 
                        secondary={`${pet?.gender || ""} ${pet?.breed || ""} ${pet.species ? pet.species : pet.speciesLookup.label} ${pet.deceased ? "+deceased" : ""}`}
                    />
                </ListItemButton>
            </ListItem>
            ))}
        </List>
        <Button onClick={handleAddPet} variant='outlined' fullWidth>Add Pet</Button>
        <PetView 
            handleEditPet={handleEditPet}
            pet={selectedPet}
            open={viewOpen}
            onClose={() => setViewOpen(false)}
        />
        <PetForm 
            successCallback={onSavePetSuccess}
            petOwner={petOwner}
            pet={selectedPet}
            mode={petFormMode}
            open={petFormOpen}
            onClose={() => {
                setPetFormOpen(false);
            }}
        />
        </>
    );
}